import { Doc } from '@dadohr/shared/domainTypes/document';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  UserImportDataSourceBambooHr,
  UserImportDataSourceStatus,
  UserImportMapping,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import { Button, DialogActions, DialogContent } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Users } from 'lucide-react';
import { useState } from 'react';
import shortid from 'shortid';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { DeleteButton } from '../../../../../../components/DeletionConfirmation';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import {
  removeDoc,
  setDoc,
  updateDoc
} from '../../../../../../services/firebase';
import { now } from '../../../../../../services/time';
import { useUserFields } from '../../../../../../services/user';
import { CustomImportMappingForm2 } from '../CustomImportMappingForm';
import { Img, ImportSourceListItem } from './common';
import { IMPORT_META } from './meta';

const META = IMPORT_META.BAMBOOHR;

const DEFAULT_MAPPINGS: UserImportMapping[] = [
  {
    dadoKey: 'alternateId',
    otherKey: 'employeeNumber'
  },
  {
    dadoKey: 'firstName',
    otherKey: 'firstName'
  },
  {
    dadoKey: 'preferredName',
    otherKey: 'preferredName'
  },
  {
    dadoKey: 'lastName',
    otherKey: 'lastName'
  },
  {
    dadoKey: 'email',
    otherKey: 'workEmail'
  },
  {
    dadoKey: 'personalEmail',
    otherKey: 'homeEmail'
  },
  {
    dadoKey: 'role',
    otherKey: 'jobTitle'
  },
  {
    dadoKey: 'department',
    otherKey: 'department'
  },
  {
    dadoKey: 'location',
    otherKey: 'location'
  },
  {
    dadoKey: 'hireDate',
    otherKey: 'hireDate'
  },
  {
    dadoKey: 'terminationDate',
    otherKey: 'terminationDate'
  },
  {
    dadoKey: 'active',
    otherKey: 'status'
  },
  {
    dadoKey: 'managerId',
    otherKey: 'supervisorEmail'
  }
];

// TODO
// Define default mappings for bamboo
// Change setup -> acknowledge the mappings first
// No need to hide them, can always expand as it's the only thing to set up there

export const BambooHrSetupFlow = ({
  orgId,
  onClose,
  customFields
}: {
  orgId: string;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();
  const { id: userId } = useCurrentUser();
  const userFields = useUserFields(customFields);

  const [state, setState] = useState({
    mappings: DEFAULT_MAPPINGS
  });

  // return <BambooHrAutoSetupSink orgId={orgId} userId={userId} />;
  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={META.logoUrl} />
        </FormRow>
        <CustomImportMappingForm2
          additionalCompositeFields={['$FULL_NAME$']}
          userFields={userFields}
          integrationName="BambooHR"
          name={t('Default Field Mapping')}
          mappings={state.mappings}
          onChange={(mappings) => setState((s) => ({ ...s, mappings }))}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <ButtonWithPromise
          color="primary"
          variant="contained"
          pending={`${t('Saving')}...`}
          onClick={async () => {
            const nextSource: Doc<UserImportDataSourceBambooHr> = {
              id: shortid(),
              collection: FS.userImportDataSources,
              data: {
                orgId: orgId,
                type: UserImportSourceType.BAMBOO_HR,
                status: UserImportDataSourceStatus.PENDING,
                detailedStatus: '',
                lastUpdate: null,
                createdBy: userId,
                createdAt: now(),
                customMappings: state.mappings
              }
            };
            await setDoc(nextSource);
            onClose();
          }}
        >
          {t('Add source')}
        </ButtonWithPromise>
      </DialogActions>
    </>
  );
};

export const BambooExistingSetup = ({
  d,
  onClose,
  customFields
}: {
  d: Doc<UserImportDataSourceBambooHr>;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);

  const [mappings, setMappings] = useState(d.data.customMappings);

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={META.logoUrl} />
        </FormRow>
        <FormRow>
          <Alert severity="success" icon={<Users />}>
            {t(
              'BambooHR is currently setup as a user import source. New or updated users in Bamboo will be imported to Dado.'
            )}
          </Alert>
        </FormRow>
        <CustomImportMappingForm2
          additionalCompositeFields={['$FULL_NAME$']}
          userFields={userFields}
          integrationName="BambooHR"
          name={t('Default Field Mapping')}
          mappings={mappings}
          onChange={(mappings) => setMappings((s) => ({ ...s, mappings }))}
        />
      </DialogContent>
      <DialogActionsWithSlots
        left={
          <DeleteButton
            variant="contained"
            color="secondary"
            onDelete={() => removeDoc(d).then(onClose)}
            keyword={d.id}
            dialogTitle={t('Remove integration') + '?'}
            buttonAction={t('Remove integration')}
          >
            {t('Remove integration')}
          </DeleteButton>
        }
        right={
          <>
            <Button onClick={onClose}>{t('Close')}</Button>
            <ButtonWithPromise
              color="primary"
              variant="contained"
              pending={`${t('Updating')}...`}
              onClick={async () => {
                d.data.customMappings = mappings;
                await updateDoc(d, (x) => ({
                  customMappings: x.customMappings
                }));
              }}
            >
              {t('Update')}
            </ButtonWithPromise>
          </>
        }
      />
    </>
  );
};

export const ImportSourceRowBambooHr = ({
  d
}: {
  d: Doc<UserImportDataSourceBambooHr>;
}) => {
  return (
    <ImportSourceListItem
      enabled={d.data.status === UserImportDataSourceStatus.OK}
      name={META.name}
      logoUrl={META.smallLogoUrl}
    />
  );
};
